import { Component, inject, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { MenuController, ModalController } from '@ionic/angular/standalone';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Subscription, filter, switchMap } from 'rxjs';
import { IonApp, IonSplitPane, IonMenu, IonHeader, IonToolbar, IonTitle,
         IonContent, IonList, IonItem, IonIcon, IonLabel, IonRouterOutlet, IonButton, IonMenuToggle, IonButtons, IonMenuButton } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { briefcaseOutline, documentTextOutline, cubeOutline, personOutline, menuOutline, addOutline } from 'ionicons/icons';
import { ClerkService } from 'ngx-clerk-iliad';
import { environment } from 'src/environments/environment';
import { UserService } from 'src/app/services/user.service';
import { RoleService } from 'src/app/services/role.service';
import { ThemeService } from 'src/app/services/theme.service';
import { User } from 'src/app/models/user.model';
import { H } from 'highlight.run';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { APP_VERSION } from 'src/environments/build-info';

// Add the icons we're using
addIcons({
  'briefcase': briefcaseOutline,
  'document-text': documentTextOutline,
  'cube': cubeOutline,
  'person': personOutline,
  'menu-outline': menuOutline,
  'add': addOutline
});

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    IonApp,
    IonSplitPane,
    IonMenu,
    IonHeader,
    IonToolbar,
    IonButtons,
    IonMenuButton,
    IonTitle,
    IonContent,
    IonList,
    IonItem,
    IonIcon,
    IonLabel,
    IonRouterOutlet,
    IonButton,
    IonMenuToggle
  ]
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  // Common pages for all users (always visible)
  commonPages = [
    { title: 'Home', url: '/home', icon: 'person' },
    { title: 'Account', url: '/account', icon: 'person' }
  ];

  // Combined pages based on user role
  appPages: any[] = [];

  isAuthRoute = false;
  isHomePage = false;
  isAuthenticated = false;
  isDarkMode = false;
  userName = '';
  userInitial = '?';
  userAvatarUrl: string | null = null;

  // Build number from environment
  buildNumber = environment.buildNumber;
  // Display version: main (package.json) + sub (build number)
  displayVersion = '';

  // References to logo elements for cleanup
  private darkLogoElement: HTMLImageElement | null = null;
  private lightLogoElement: HTMLImageElement | null = null;

  // References for cleanup of observers and timeouts
  private logoObserver: MutationObserver | null = null;
  private logoObserverTimeout: number | null = null;

  private routerSubscription!: Subscription;
  private authSubscription!: Subscription;
  private roleSubscription!: Subscription;
  private themeSubscription!: Subscription;

  private router = inject(Router);
  private menu = inject(MenuController);
  private modalCtrl = inject(ModalController);
  private _clerk = inject(ClerkService);
  private userService = inject(UserService);
  private roleService = inject(RoleService);
  private themeService = inject(ThemeService);
  private analytics = inject(AnalyticsService);

  constructor() {
    this._clerk.__init({
      publishableKey: environment.clerkPublishableKey,
      clerkJSUrl: 'https://cdn.jsdelivr.net/npm/@clerk/clerk-js@5/dist/clerk.browser.js'
    });

    // Ensure the menu has basic items immediately
    this.appPages = [...this.commonPages];

    // Initialize isAuthRoute based on the current URL
    this.isAuthRoute = this.router.url.includes('/auth');
    console.log('Initial URL:', this.router.url);
    console.log('Initial isAuthRoute:', this.isAuthRoute);
    console.log('[AppComponent] Build:', this.buildNumber, 'Production:', environment.production, 'API:', environment.apiUrl);

    // Compose display version once on construction
    this.displayVersion = `v${APP_VERSION} (${this.buildNumber})`;

    // Initialize Google Analytics if enabled for this environment
    try { this.analytics.init(); } catch {}
  }

  async openFeedback() {
    const { FeedbackModalComponent } = await import('src/app/components/feedback-modal/feedback-modal.component');
    const modal = await this.modalCtrl.create({ component: FeedbackModalComponent });
    await modal.present();
  }

  private resolveUserName(user: User | null | undefined): string {
    if (!user) return '';
    return (
      user.full_legal_name?.trim() ||
      user.name?.trim() ||
      [user.first_name, user.last_name].filter(Boolean).join(' ').trim() ||
      user.profile_username?.trim() ||
      user.email ||
      ''
    );
  }

  private updateUserIdentity(user: User | null | undefined) {
    this.userName = this.resolveUserName(user);
    const initial = (this.userName || '').trim().charAt(0);
    this.userInitial = initial ? initial.toUpperCase() : '?';
    this.userAvatarUrl = user?.avatar || null;

    // Identify user in Highlight with minimal PII: only stable id + environment
    try {
      if (user) {
        H.identify(String(user.id ?? user.email ?? 'unknown'), {
          environment: environment.production ? 'production' : 'development',
          // Include sub-version as a trait for convenience in searches
          buildNumber: String(this.buildNumber),
          appVersion: APP_VERSION
        } as any);
      }
    } catch {}
  }

  ngOnInit() {
    // Subscribe to router events to track auth routes
    this.routerSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      // Check if the current URL includes '/auth'
      this.isAuthRoute = event.url.includes('/auth');
      // Check if the current URL is home
      this.isHomePage = event.url === '/home' || event.url === '/' || event.url === '' || event.url === '/account';

      console.log('Current URL:', event.url);
      console.log('isAuthRoute:', this.isAuthRoute);
      console.log('isHomePage:', this.isHomePage);

      // Send GA4 page_view on every successful navigation
      const path = (event as any).urlAfterRedirects || event.url;
      try { this.analytics.pageView(path, document?.title, typeof window !== 'undefined' ? window.location.href : undefined); } catch {}
    });

    // Subscribe to authentication state and handle role-based redirection
    this.authSubscription = this._clerk.user$.subscribe(user => {
      const wasAuthenticated = this.isAuthenticated;
      this.isAuthenticated = !!user;
      console.log('Authentication state:', this.isAuthenticated);

      // If user just became authenticated
      if (!wasAuthenticated && this.isAuthenticated) {
        if (this.isAuthRoute) {
            // Fetch user profile
            this.userService.getCurrentUser().subscribe(user => {
              this.updateUserIdentity(user);
              // Set GA user_id to stable app user ID (no PII)
              try { this.analytics.setUserId(user?.id ? String(user.id) : undefined); } catch {}
              // Update navigation menu immediately
              this.updateNavigationMenu();
              console.log('Updated navigation menu after login');
              this.router.navigate(['/home']);
            });
          }
      }

      // If user logged out, reset Highlight session to de-identify
      if (wasAuthenticated && !this.isAuthenticated) {
        try { H.stop(); H.start({ forceNew: true }); } catch {}
        // Clear GA user_id on sign-out
        try { this.analytics.setUserId(undefined); } catch {}
      }
    });

    // Subscribe to role service to update navigation menu based on user profile
    this.roleSubscription = this.roleService.getCurrentUser().subscribe(user => {
      this.updateUserIdentity(user);
      // Update navigation menu
      this.updateNavigationMenu();
    });

    // Subscribe to theme changes to ensure proper logo display
    this.themeSubscription = this.themeService.darkMode$.subscribe(isDark => {
      console.log('Theme changed, dark mode:', isDark);
      console.log('Body classes:', document.body.classList.toString());

      // Update component property to track theme state
      this.isDarkMode = isDark;

      // Update logo visibility based on current theme
      this.updateLogoVisibility(isDark);
    });
  }

  ngAfterViewInit() {
    console.log('AppComponent view initialized');

    // Get the current theme state
    // Note: We need to access the current value of the BehaviorSubject
    // This is a workaround since we can't directly access the private property
    let currentTheme = false;
    this.themeService.darkMode$.subscribe(isDark => {
      currentTheme = isDark;
    }).unsubscribe(); // Immediately unsubscribe to avoid memory leaks

    console.log('Initial theme on view init:', currentTheme ? 'dark' : 'light');

    // Update logo visibility based on initial theme
    this.updateLogoVisibility(currentTheme);
  }

  onAvatarError() {
    // If the avatar fails to load, clear the URL so template falls back to initial
    this.userAvatarUrl = null;
  }

  ngOnDestroy() {
    // Unsubscribe from all subscriptions
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
    if (this.roleSubscription) {
      this.roleSubscription.unsubscribe();
    }
    if (this.themeSubscription) {
      this.themeSubscription.unsubscribe();
    }

    // Clean up event handlers on logo elements
    if (this.darkLogoElement) {
      this.darkLogoElement.onload = null;
      this.darkLogoElement.onerror = null;
      this.darkLogoElement = null;
    }

    if (this.lightLogoElement) {
      this.lightLogoElement.onload = null;
      this.lightLogoElement.onerror = null;
      this.lightLogoElement = null;
    }

    // Clean up any active logo observer and timeout
    this.cleanupLogoObserver();

    console.log('AppComponent destroyed, all resources cleaned up');
  }

  /**
   * Updates logo visibility based on the current theme
   * @param isDark Whether dark mode is active
   */
  private updateLogoVisibility(isDark: boolean) {
    console.log('Updating logo visibility, dark mode:', isDark);

    // Skip logo update only if we're on an auth route (auth page handles its own logo)
    // but allow updates for navbar logo regardless of authentication state
    if (this.isAuthRoute) {
      console.log('Skipping logo visibility update - on auth route which handles its own logo');
      return;
    }

    // Force reload images by updating src with current timestamp
    const timestamp = new Date().getTime();
    const darkPngPath = 'assets/images/logo-dark-theme.png';
    const lightPngPath = 'assets/images/logo-light-theme.png';

    // Set PNG paths with timestamp
    const darkSrc = darkPngPath + '?v=' + timestamp;
    const lightSrc = lightPngPath + '?v=' + timestamp;

    console.log('Setting image sources with timestamp:', timestamp);
    console.log('Dark logo src:', darkSrc);
    console.log('Light logo src:', lightSrc);

    // Use MutationObserver to detect when logo elements are available in the DOM
    this.waitForLogoElements().then(([darkLogo, lightLogo]) => {
      console.log('Found logo elements, force-updating sources to ensure reload');

      // Store references to logo elements for cleanup
      this.darkLogoElement = darkLogo;
      this.lightLogoElement = lightLogo;

      // Add load event listeners to verify images are loading
      if (this.darkLogoElement) {
        this.darkLogoElement.onload = () => console.log('Dark logo loaded successfully');
        this.darkLogoElement.onerror = () => console.error('Failed to load dark logo');
        // Set new src to force reload
        this.darkLogoElement.src = darkSrc;
      }

      if (this.lightLogoElement) {
        this.lightLogoElement.onload = () => console.log('Light logo loaded successfully');
        this.lightLogoElement.onerror = () => console.error('Failed to load light logo');
        // Set new src to force reload
        this.lightLogoElement.src = lightSrc;
      }

      // Note: visibility is now handled by CSS in app.component.scss
      console.log('Logo sources updated. Visibility is controlled by CSS.');
    }).catch(error => {
      console.error('Failed to find logo elements:', error);
    });
  }

  /**
   * Waits for logo elements to be available in the DOM
   * @returns Promise that resolves with the logo elements when they're available
   */
  private waitForLogoElements(): Promise<[HTMLImageElement, HTMLImageElement]> {
    return new Promise((resolve, reject) => {
      // Clean up any existing observer and timeout
      this.cleanupLogoObserver();

      // First, check if elements already exist
      const darkLogo = document.querySelector('.logo-dark') as HTMLImageElement;
      const lightLogo = document.querySelector('.logo-light') as HTMLImageElement;

      if (darkLogo && lightLogo) {
        console.log('Logo elements already in DOM');
        resolve([darkLogo, lightLogo]);
        return;
      }

      console.log('Logo elements not found, setting up observer');

      // Set up a MutationObserver to watch for changes to the DOM
      this.logoObserver = new MutationObserver((mutations, obs) => {
        const darkLogo = document.querySelector('.logo-dark') as HTMLImageElement;
        const lightLogo = document.querySelector('.logo-light') as HTMLImageElement;

        if (darkLogo && lightLogo) {
          console.log('Logo elements found in DOM via observer');
          this.cleanupLogoObserver(); // Clean up observer and timeout
          resolve([darkLogo, lightLogo]);
        }
      });

      // Start observing with a configuration to watch for child list changes
      this.logoObserver.observe(document.body, {
        childList: true,
        subtree: true
      });

      // Set a timeout to avoid waiting indefinitely
      this.logoObserverTimeout = window.setTimeout(() => {
        console.log('Timed out waiting for logo elements');
        this.cleanupLogoObserver();
        reject(new Error('Timed out waiting for logo elements'));
      }, 5000); // 5 second timeout
    });
  }

  /**
   * Cleans up any active logo observer and timeout
   */
  private cleanupLogoObserver(): void {
    // Disconnect observer if it exists
    if (this.logoObserver) {
      this.logoObserver.disconnect();
      this.logoObserver = null;
      console.log('Logo observer disconnected');
    }

    // Clear timeout if it exists
    if (this.logoObserverTimeout !== null) {
      window.clearTimeout(this.logoObserverTimeout);
      this.logoObserverTimeout = null;
      console.log('Logo observer timeout cleared');
    }
  }

  /**
   * Update the navigation menu
   */
  updateNavigationMenu() {
    this.appPages = [...this.commonPages];
    console.log('Updated navigation menu');
  }

  navigateTo(url: string) {
    this.router.navigateByUrl(url);
    this.menu.close();
  }
}
